/**
 * Комиссия ноги: процент, который режет выплату, не показанный кэф.
 *
 * У чужого калькулятора это галка внизу и поле в настройках конторы.
 * Здесь — тихий знак на плитке: слово не стоит, пока человек не открыл поле.
 * Ноль законен: рынок без комиссии, и придумывать 5 % нельзя.
 */

import { useEffect, useRef, useState } from 'react';

import { readOwnFee } from '../lib/row';

export function FeeEdit({
  form,
  locked,
  onLock,
  onUnlock,
}: {
  form: string;
  locked: boolean;
  onLock: (form: string) => void;
  onUnlock: () => void;
}): React.ReactElement {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(form);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!edit) setDraft(form);
  }, [edit, form]);

  useEffect(() => {
    if (!edit) return;
    const node = field.current;
    if (node === null) return;
    node.focus();
    node.select();
  }, [edit]);

  const commit = (value: string, emptyUnlock: boolean): boolean => {
    const raw = value.trim().replace(/\s/g, '').replace(',', '.').replace(/%/g, '');
    if (raw === '') {
      if (emptyUnlock) onUnlock();
      return emptyUnlock;
    }
    const next = readOwnFee(value);
    if (next === null) return false;
    onLock(next);
    return true;
  };

  return (
    <span className={`nx-fee${locked ? ' own' : ''}${edit ? ' edit' : ''}`}>
      {edit ? (
        <input
          ref={field}
          className="nx-fee__in"
          value={draft}
          inputMode="decimal"
          aria-label="комиссия ноги в процентах"
          onChange={(event) => {
            const value = event.target.value;
            setDraft(value);
            commit(value, false);
          }}
          onBlur={() => {
            if (!commit(field.current?.value ?? draft, true)) setDraft(form);
            setEdit(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.currentTarget.blur();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              setDraft(form);
              setEdit(false);
            }
            event.stopPropagation();
          }}
          onClick={(event) => event.stopPropagation()}
        />
      ) : (
        <button
          type="button"
          className="nx-fee__go"
          title={locked ? `комиссия ${form} %` : 'комиссия ноги'}
          aria-label={locked ? `комиссия ${form} процентов` : 'задать комиссию ноги'}
          onClick={(event) => {
            event.stopPropagation();
            setDraft(form === '' ? '0' : form);
            setEdit(true);
          }}
        >
          {locked ? `комиссия ${form} %` : '%'}
        </button>
      )}
      {locked && !edit && (
        <button
          type="button"
          className="nx-fee__off"
          title="без своей комиссии"
          aria-label="сбросить комиссию"
          onClick={(event) => {
            event.stopPropagation();
            onUnlock();
          }}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M4.5 4.5 11.5 11.5M11.5 4.5 4.5 11.5" />
          </svg>
        </button>
      )}
    </span>
  );
}
